import React, { useState, useEffect } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Hidden,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Link as RouterLink, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
//import { formatDistance } from 'date-fns';
import api from "../../pages/api/base-url";
import { Application, LinkToShare } from "../apps-components/apps-types";
import LinkProgress from "../apps-components/LinkProgress";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import reportWebVitals from "../../reportWebVitals";
import { formatDistance } from "date-fns";
import LinkIcon from "@mui/icons-material/Link";
import { Skeleton4LastActivities } from "./LastActivities";
import { enUS, it } from "date-fns/locale";
import HttpsIcon from "@mui/icons-material/Https";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";

export function LinkItem(props: { link: LinkToShare }) {
  const [data, setData] = useState<Application | undefined>();
  const { t } = useTranslation();

  useEffect(() => {
    let isCancelled = false;
    const findAppName = async () => {
      try {
        const response = await api.get(`/apps/${props.link.appId}`);
        if (!isCancelled) {
          setData(response.data);
        }
      } catch (err: any) {
        if (err.response) {
          //Not in 200 response range
          console.log(err.response.data);
          console.log(err.response.status);
        } else {
          console.log(`Error: ${err.message}`);
        }
      }
    };
    findAppName();
    return () => {
      isCancelled = true;
    };
  }, [props.link.appId]);

  return data && props.link ? (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <NavLink
          to={`applications/${data.name}`}
          style={{ textDecoration: "none" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Badge
              badgeContent={
                <LinkIcon sx={{ color: "white", width: 18, height: 18 }} />
              }
              color="info"
              overlap="circular"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              sx={{ backgroundColor: "white" }}
            >
              <Avatar
                alt={data.name}
                src={`${process.env.REACT_APP_BACKEND_BASE_URL}apps/${data.id}/icon`}
                sx={{ width: 40, height: 40, borderRadius: 1.5 }}
              />
            </Badge>
            <Box sx={{ width: 280 }}>
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.primary", textDecoration: "none" }}
                  noWrap
                >
                  {data.name}
                </Typography>
                {props.link.isAuthenticated ? (
                  <HttpsIcon color="action" sx={{ width: 18, height: 18 }} />
                ) : (
                  <NoEncryptionGmailerrorredIcon
                    color="action"
                    sx={{ width: 18, height: 18 }}
                  />
                )}
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary" }}
                noWrap
              >
                {props.link.versions[1]
                  ? props.link.versions[0].versionNumber +
                    ", " +
                    props.link.versions[1]?.versionNumber
                  : props.link.versions[0].versionNumber}
              </Typography>
            </Box>
            <Hidden smDown>
              <Box sx={{ alignItems: "center", width: 280 }}>
                <Typography
                  variant="caption"
                  sx={{
                    pr: 3,
                    flexShrink: 0,
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  {t("Link Generated")}
                </Typography>
              </Box>
            </Hidden>
            <Hidden smDown>
              <Box sx={{ alignItems: "center", width: 280 }}>
                <Typography
                  variant="caption"
                  sx={{
                    pr: 3,
                    flexShrink: 0,
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  {formatDistance(new Date(props.link.updated_at), new Date(), {
                    locale: localStorage.getItem("lng") === "it" ? it : enUS,
                  })}
                </Typography>
              </Box>
              {/*  */}
              <Box sx={{ alignItems: "center", width: 280 }}>
                <LinkProgress
                  validity_end_date={props.link.validity_end}
                  validity_start_date={props.link.validity_start}
                />
              </Box>
            </Hidden>
          </Stack>
        </NavLink>
      </Stack>
    </Box>
  ) : (
    <Skeleton4LastActivities />
  );
}

function LastLinks(props: { reverseLinks: LinkToShare[]; isLoading: boolean }) {
  const { t } = useTranslation();
  const skeletonArray = Array(3).fill("");

  return (
    <Card
      sx={{
        boxShadow: 5,
        borderRadius: 1.5,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CardHeader title={t("Last Links")} />
      {props.isLoading ? (
        <Stack spacing={3} sx={{ p: 3, pr: 0 }}>
          {skeletonArray.map((skeleton, index) => (
            <Skeleton4LastActivities key={index} />
          ))}
        </Stack>
      ) : props.reverseLinks.length > 0 ? (
        <Stack spacing={3} sx={{ p: 3, pr: 0 }}>
          {props.reverseLinks.map((item, index) => (
            <LinkItem key={index} link={item} />
          ))}
        </Stack>
      ) : (
        <NoDataToBeShown />
      )}

      <Divider />

      <Box sx={{ p: 2, textAlign: "right" }}>
        <Button
          to="/links"
          size="small"
          color="inherit"
          component={RouterLink}
          endIcon={<ArrowForwardIosIcon />}
        >
          {t("View All")}
        </Button>
      </Box>
    </Card>
  );
}

export default LastLinks;

reportWebVitals();
